import React, { useState, useEffect } from 'react';
import { Users, Mail, Check, X, UserPlus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function ConnectionsTab() {
  const [connections, setConnections] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [connectionType, setConnectionType] = useState('coworker');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConnections();
    fetchPendingRequests();
  }, []);

  const fetchConnections = async () => {
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/connections`, {
        withCredentials: true,
        headers
      });
      setConnections(data);
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/connections/pending`, {
        withCredentials: true,
        headers
      });
      setPendingRequests(data);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API_URL}/api/connections/invite`,
        { email: inviteEmail, connection_type: connectionType },
        { withCredentials: true, headers }
      );
      toast.success('Invitation sent');
      setInviteEmail('');
      setShowInviteForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (connectionId) => {
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API_URL}/api/connections/${connectionId}/accept`,
        {},
        { withCredentials: true, headers }
      );
      toast.success('Connection accepted');
      fetchConnections();
      fetchPendingRequests();
    } catch (error) {
      toast.error('Failed to accept connection');
    }
  };

  const handleReject = async (connectionId) => {
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API_URL}/api/connections/${connectionId}/reject`,
        {},
        { withCredentials: true, headers }
      );
      toast.success('Connection rejected');
      fetchPendingRequests();
    } catch (error) {
      toast.error('Failed to reject connection');
    }
  };

  return (
    <div className="space-y-4" data-testid="connections-tab">
      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <div>
          <h3 className="text-xs tracking-wider uppercase text-[#78716C] mb-2">Pending Requests</h3>
          <div className="space-y-2">
            {pendingRequests.map((req) => (
              <div
                key={req.connection_id}
                className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-3"
                data-testid={`pending-request-${req.connection_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-[#1C1917]">{req.sender_details?.name}</p>
                    <p className="text-xs text-[#78716C]">{req.sender_details?.email}</p>
                  </div>
                  <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#F5F2EA] text-[#78716C]">
                    {req.connection_type}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAccept(req.connection_id)}
                    className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium transition-colors bg-[#2C4C3B] text-white hover:bg-[#243D30] flex-1"
                    data-testid="accept-connection-button"
                  >
                    <Check size={12} className="mr-1" /> Accept
                  </button>
                  <button
                    onClick={() => handleReject(req.connection_id)}
                    className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] flex-1"
                    data-testid="reject-connection-button"
                  >
                    <X size={12} className="mr-1" /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Form */}
      {showInviteForm ? (
        <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
          <h3 className="text-lg tracking-tight font-medium text-[#1C1917] mb-3">Invite Connection</h3>
          <form onSubmit={handleInvite} className="space-y-3">
            <div>
              <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">Email</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] placeholder-[#78716C] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
                placeholder="colleague@example.com"
                required
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">Relationship</label>
              <select
                value={connectionType}
                onChange={(e) => setConnectionType(e.target.value)}
                className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
                data-testid="connection-type-select"
              >
                <option value="coworker">Coworker</option>
                <option value="friend">Friend</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] flex-1"
                data-testid="cancel-invite-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-[#E7E5E4] focus:outline-none bg-[#2C4C3B] text-white hover:bg-[#243D30] flex-1 disabled:opacity-50"
                data-testid="send-invite-button"
              >
                {loading ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <button
          onClick={() => setShowInviteForm(true)}
          className="w-full inline-flex items-center justify-center rounded-lg px-4 py-3 text-sm font-medium transition-colors border-2 border-dashed border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] hover:border-[#2C4C3B]"
          data-testid="invite-connection-button"
        >
          <UserPlus size={16} className="mr-2" />
          Invite Connection
        </button>
      )}

      {/* Connections List */}
      <div>
        <h3 className="text-xs tracking-wider uppercase text-[#78716C] mb-2">My Connections</h3>
        {connections.length === 0 ? (
          <div className="text-center py-8" data-testid="empty-connections">
            <Users size={48} className="mx-auto text-[#E7E5E4] mb-3" />
            <p className="text-sm text-[#78716C]">No connections yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {connections.map((conn) => {
              const otherUser = conn.user_details;
              if (!otherUser) return null;

              return (
                <div
                  key={conn.connection_id}
                  className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-3 flex items-center justify-between"
                  data-testid={`connection-${conn.connection_id}`}
                >
                  <div>
                    <p className="text-sm font-medium text-[#1C1917]">{otherUser.name}</p>
                    <p className="text-xs text-[#78716C]">{otherUser.email}</p>
                  </div>
                  <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#EAF0EC] text-[#2C4C3B]">
                    {conn.connection_type}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}